import {
  queryOptions,
  skipToken,
  useQueries,
  useQuery
} from '@tanstack/react-query'
import { useAuth } from './useAuth'
import { AvdelingResponse } from '../types/types'
import { throwOnError } from '../utils/throwOnError'

type AuthFetch = ReturnType<typeof useAuth>['authFetch']

export const getAvdelingerOptions = (
  authFetch: AuthFetch,
  regionId?: string
) => {
  return queryOptions({
    staleTime: 1000 * 60 * 60 * 24, // 1 day
    queryKey: ['avdelinger', regionId],
    queryFn:
      regionId != null && regionId.length > 0
        ? async () => {
            const { data, error } = await authFetch().GET(
              '/orgenhet-api/regioner/{regionid}/avdelinger',
              {
                params: {
                  path: {
                    regionid: regionId
                  }
                }
              }
            )
            if (error) {
              throwOnError(error)
            }
            const response: AvdelingResponse = data as AvdelingResponse
            return response._embedded?.avdelingList ?? []
          }
        : skipToken
  })
}

export const useGetAvdelingerQuery = (regionId?: string) => {
  const { authFetch } = useAuth()
  return useQuery(getAvdelingerOptions(authFetch, regionId))
}

export const useGetAvdelingerQueries = (regionIds: string[]) => {
  const { authFetch } = useAuth()
  return useQueries({
    queries: regionIds.map((regionId) =>
      getAvdelingerOptions(authFetch, regionId)
    ),
    combine: (results) => ({
      data: results.flatMap((result) =>
        Array.isArray(result.data) ? result.data : []
      ),
      isLoading: results.some((result) => result.isLoading),
      isError: results.some((result) => result.isError),
      error: results.find((result) => result.error != null)?.error
    })
  })
}

export const useGetAvdelingerMapQuery = () => {
  const { authFetch } = useAuth()
  return useQuery({
    staleTime: 1000 * 60 * 60 * 24, // 1 day
    queryKey: ['avdelinger'],
    queryFn: async () => {
      const { data, error } = await authFetch().GET('/orgenhet-api/avdelinger')
      if (error) {
        throwOnError(error)
      }
      return data
    }
  })
}
